import React, { useId, useMemo, useRef, useState } from 'react';
import { suggestCities } from '../lib/ethiopianCities';

interface Props {
  id: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  maxLength?: number;
  placeholder?: string;
}

/**
 * Free-text city field that suggests Ethiopian cities while typing.
 * Any text is accepted (small towns may not be listed); a suggestion only
 * fills the field. Keyboard: ↓/↑ move, Enter picks, Esc closes.
 */
export default function CityCombobox({ id, value, onChange, className = '', maxLength, placeholder }: Props) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const blurTimer = useRef<number | undefined>(undefined);
  const items = useMemo(() => suggestCities(value), [value]);
  // Hide the list when the field already holds exactly one suggestion.
  const exact = items.length === 1 && items[0].name.toLowerCase() === value.trim().toLowerCase();
  const show = open && items.length > 0 && !exact;

  const pick = (name: string) => {
    onChange(name);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (items.length ? (a + 1) % items.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (items.length ? (a <= 0 ? items.length - 1 : a - 1) : -1));
    } else if (e.key === 'Enter' && show && active >= 0) {
      e.preventDefault();
      pick(items[active].name);
    } else if (e.key === 'Escape' && show) {
      e.preventDefault();
      e.stopPropagation(); // close the list, not the surrounding drawer
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={show}
        aria-controls={listId}
        aria-activedescendant={show && active >= 0 ? `${listId}-${active}` : undefined}
        className={className}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = window.setTimeout(() => setOpen(false), 120); }}
        onKeyDown={onKeyDown}
      />
      {show && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Suggested cities"
          className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-[12px] border border-line-strong bg-surface py-1 shadow-lg"
        >
          {items.map((c, i) => (
            <li
              key={`${c.name}-${c.region}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); window.clearTimeout(blurTimer.current); pick(c.name); }}
              onMouseEnter={() => setActive(i)}
              className={`flex cursor-pointer items-baseline justify-between gap-3 px-3.5 py-2 text-sm ${i === active ? 'bg-surface-2' : ''}`}
            >
              <span className="truncate text-ink">{c.name}</span>
              <span className="shrink-0 text-xs text-muted">{c.region}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
